import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Types
export interface ComplianceWorkflow {
	id: string;
	name: string;
	status: "draft" | "active" | "paused" | "completed" | "cancelled" | "deleted";
	completion_percentage: number;
	estimated_duration_days: number;
	target_completion_date?: string;
	current_step_id?: string;
	jurisdiction: string;
	device_class: string;
	device_type: string;
	device_category: string;
	configuration?: Record<string, any>;
	created_at: string;
	updated_at: string;
}

export interface ComplianceStep {
	id: string;
	step_number: number;
	title: string;
	status: "pending" | "in_progress" | "completed" | "blocked" | "skipped";
	estimated_days: number;
	actual_days?: number;
	due_date?: string | null;
	required_forms: string[];
	required_documents: string[];
	comments?: string | null;
	solution?: string | null;  // AI-generated guidance/solution
	attachments?: string[] | null;  // List of file URLs
	configuration?: {
		form_downloads?: Array<{
			form_code?: string;
			form_name?: string;
			form_download_url?: string;
			checklist_download_url?: string;
			download_url?: string;
		}>;
		form_filling?: boolean;
		form_code?: string;
		form_name?: string;
		form_download_url?: string;
		checklist_download_url?: string;
		[key: string]: any;
	};
}

export interface WorkflowCreateRequest {
	name: string;
	description?: string;
	jurisdiction: string;
	device_class: string;
	device_type: string;
	device_category: string;
	configuration?: Record<string, any>;
}

export interface WorkflowStatusResponse {
	workflow: ComplianceWorkflow;
	steps: ComplianceStep[];
}

export interface ChatMessage {
	id: string;
	type: string;
	content: string;
	timestamp: string;
	forms?: Array<{
		form_id?: string;
		form_code?: string;
		form_name?: string;
		form_download_url?: string;
		checklist_download_url?: string;
		has_form_file?: boolean;
		has_checklist?: boolean;
		[key: string]: any;
	}>;
}

export interface ChatWorkflowCreateRequest {
	name: string;
	description?: string;
	jurisdiction: string;
	device_class: string;
	device_type?: string;
	device_category?: string;
	chat_context: any;
	messages: ChatMessage[];
	use_output_based_conversion?: boolean;
}

class WorkflowService {
	constructor() {
		axios.interceptors.response.use(
			(response) => response,
			(error) => {
				if (
					axios.isAxiosError(error) &&
					error.response?.status === 401 &&
					!error.config?.url?.includes("/api/v1/auth/login")
				) {
					localStorage.removeItem("gamora_token");
					localStorage.removeItem("gamora_role");
					localStorage.removeItem("gamora_user_id");
					localStorage.removeItem("gamora_session_id");
					if (typeof window !== "undefined" && window.location.pathname !== "/") {
						window.location.href = "/";
					}
				}
				return Promise.reject(error);
			}
		);
	}

	private getAuthHeaders() {
		const token = localStorage.getItem("gamora_token");
		const headers = {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		};
		return headers;
	}

	// Download a generated step artifact (e.g., standards_mapping_csv)
	async downloadStepArtifact(stepId: string, artifactKey: string): Promise<Blob> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/artifacts/${encodeURIComponent(artifactKey)}`,
				{
					headers: this.getAuthHeaders(),
					responseType: "blob",
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error downloading step artifact:", error);
			throw error;
		}
	}

	// Update an existing generated step artifact (used by the in-app sheet editor)
	async updateStepArtifact(stepId: string, artifactKey: string, content: string): Promise<void> {
		try {
			await axios.put(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/artifacts/${encodeURIComponent(artifactKey)}`,
				{ content },
				{ headers: this.getAuthHeaders() }
			);
		} catch (error) {
			console.error("Error updating step artifact:", error);
			throw error;
		}
	}

	// Get all workflows for the current user
	async getWorkflows(): Promise<ComplianceWorkflow[]> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/workflows`,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching workflows:", error);
			throw error;
		}
	}

	// Get workflows with pagination
	async getWorkflowsPaginated(
		page: number = 1,
		limit: number = 10
	): Promise<{
		workflows: ComplianceWorkflow[];
		pagination: {
			page: number;
			limit: number;
			total: number;
			pages: number;
			has_next: boolean;
			has_prev: boolean;
		};
	}> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/workflows`,
				{
					headers: this.getAuthHeaders(),
					params: { page, limit },
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching workflows with pagination:", error);
			throw error;
		}
	}

	// Get a specific workflow by ID
	async getWorkflow(workflowId: string): Promise<WorkflowStatusResponse> {
		try {
			const url = `${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}/status`;
			const response = await axios.get(url, {
				headers: this.getAuthHeaders(),
			});
			return response.data;
		} catch (error) {
			console.error("Error fetching workflow:", error);
			if (axios.isAxiosError(error)) {
				console.error("Response status:", error.response?.status);
				console.error("Response data:", error.response?.data);
			}
			throw error;
		}
	}

	// Get workflow status (alias for getWorkflow)
	async getWorkflowStatus(workflowId: string): Promise<WorkflowStatusResponse> {
		return this.getWorkflow(workflowId);
	}

	// Create a new workflow
	async createWorkflow(
		workflowData: WorkflowCreateRequest
	): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/workflows`,
				workflowData,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error creating workflow:", error);
			throw error;
		}
	}

	// Start a workflow
	async startWorkflow(workflowId: string): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}/start`,
				{},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error starting workflow:", error);
			throw error;
		}
	}

	// Pause a workflow
	async pauseWorkflow(workflowId: string): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}/pause`,
				{},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error pausing workflow:", error);
			throw error;
		}
	}

	// Resume a workflow
	async resumeWorkflow(workflowId: string): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}/resume`,
				{},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error resuming workflow:", error);
			throw error;
		}
	}

	// Complete a workflow step
	async completeStep(
		stepId: string,
		completionNotes?: string,
		attachments?: string[]
	): Promise<ComplianceStep> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/complete`,
				{
					completion_notes: completionNotes,
					attachments: attachments,
				},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error completing step:", error);
			throw error;
		}
	}

	// Start a workflow step
	async startStep(stepId: string): Promise<ComplianceStep> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/start`,
				{},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error starting step:", error);
			throw error;
		}
	}

	// Run a workflow step with AI and auto-complete it
	async runStep(stepId: string): Promise<ComplianceStep> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/run`,
				{},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error running step:", error);
			throw error;
		}
	}

	// Skip a workflow step
	async skipStep(stepId: string, skipNotes?: string): Promise<ComplianceStep> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/skip`,
				{
					skip_notes: skipNotes,
				},
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error skipping step:", error);
			throw error;
		}
	}

	// Update step status
	async updateStepStatus(stepId: string, newStatus: string): Promise<ComplianceStep> {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/status`,
				null,
				{
					headers: this.getAuthHeaders(),
					params: { new_status: newStatus },
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error updating step status:", error);
			throw error;
		}
	}

	// Update workflow status (e.g., delete)
	async updateWorkflowStatus(workflowId: string, newStatus: string): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.patch(
				`${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}/status`,
				null,
				{
					headers: this.getAuthHeaders(),
					params: { new_status: newStatus },
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error updating workflow status:", error);
			throw error;
		}
	}

	// Get available jurisdictions
	async getJurisdictions(): Promise<string[]> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/jurisdictions`,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching jurisdictions:", error);
			throw error;
		}
	}

	// Get available device classes
	async getDeviceClasses(): Promise<string[]> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/device-classes`,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching device classes:", error);
			throw error;
		}
	}

	// Get device classes by jurisdiction
	async getDeviceClassesByJurisdiction(jurisdiction: string): Promise<{
		jurisdiction: string;
		device_classes: Array<{ value: string; display_name: string }>;
	}> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/device-classes/${jurisdiction}`,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching device classes by jurisdiction:", error);
			throw error;
		}
	}

	// Update step comments
	async updateStepComments(stepId: string, comments: string): Promise<void> {
		try {
			await axios.put(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/comments`,
				{ comments },
				{ headers: this.getAuthHeaders() }
			);
		} catch (error) {
			console.error(`Error updating step comments for ${stepId}:`, error);
			throw error;
		}
	}

	async createChatWorkflow(
		request: ChatWorkflowCreateRequest
	): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/workflows/chat-based`,
				request,
				{ headers: this.getAuthHeaders() }
			);
			return response.data;
		} catch (error) {
			console.error("Error creating chat-based workflow:", error);
			if (axios.isAxiosError(error)) {
				console.error("Response status:", error.response?.status);
				console.error("Response data:", error.response?.data);
			}
			throw error;
		}
	}

	// Upload file attachment for a step
	async uploadStepAttachment(stepId: string, file: File): Promise<{ file_url: string; filename: string }> {
		try {
			const formData = new FormData();
			formData.append('file', file);

			// Get auth headers but remove Content-Type for file uploads
			const authHeaders = this.getAuthHeaders();
			const { 'Content-Type': _, ...headersWithoutContentType } = authHeaders;

			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/upload`,
				formData,
				{
					headers: headersWithoutContentType,
					// Don't set Content-Type - let axios set it automatically with boundary for multipart/form-data
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error uploading step attachment:", error);
			if (axios.isAxiosError(error)) {
				console.error("Response status:", error.response?.status);
				console.error("Response data:", error.response?.data);
			}
			throw error;
		}
	}

	// Remove file attachment from a step
	async removeStepAttachment(stepId: string, fileUrl: string): Promise<void> {
		try {
			await axios.delete(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}/attachments`,
				{
					headers: this.getAuthHeaders(),
					params: { file_url: fileUrl },
				}
			);
		} catch (error) {
			console.error("Error removing step attachment:", error);
			throw error;
		}
	}

	// Update step details (title, due_date, estimated_days, required_forms, required_documents)
	async updateStepDetails(
		stepId: string,
		updates: {
			title?: string;
			due_date?: string; // ISO format datetime string
			estimated_days?: number;
			required_forms?: string[];
			required_documents?: string[];
			configuration?: Record<string, any>;
		}
	): Promise<ComplianceStep> {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}`,
				updates,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error updating step details:", error);
			throw error;
		}
	}

	// Update workflow details (name, target_completion_date)
	async updateWorkflowDetails(
		workflowId: string,
		updates: {
			name?: string;
			target_completion_date?: string; // ISO format datetime string
			configuration?: Record<string, any>;
		}
	): Promise<ComplianceWorkflow> {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}`,
				updates,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error updating workflow details:", error);
			throw error;
		}
	}

	// Create a new step in a workflow
	async createStep(
		workflowId: string,
		stepData: {
			title: string;
			description?: string;
			category?: string;
			estimated_days?: number;
			step_number?: number;
			required_forms?: string[];
			required_documents?: string[];
		}
	): Promise<ComplianceStep> {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/compliance/workflows/${workflowId}/steps`,
				stepData,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error(`Error creating step in workflow ${workflowId}:`, error);
			throw error;
		}
	}

	// Delete a step from a workflow
	async deleteStep(stepId: string): Promise<void> {
		try {
			await axios.delete(
				`${API_BASE_URL}/api/v1/compliance/steps/${stepId}`,
				{
					headers: this.getAuthHeaders(),
				}
			);
		} catch (error) {
			console.error(`Error deleting step ${stepId}:`, error);
			throw error;
		}
	}
}

export const workflowService = new WorkflowService();
